import { z } from 'zod';
import {
  jobKinds,
  MODELS,
  PIPELINE1_JOBS,
  PIPELINE2_JOBS,
  reserveFor,
  type JobKind,
} from './index';
import {
  ProviderRouteSchema,
  type ProviderRoute,
  type Routing,
  SEEDREAM,
  SEEDANCE_FAST,
  MEDIA_PRICING_VERSION,
  CLOUDFLARE_GEMINI,
  GEMINI_PRICING_VERSION,
  CLOUDFLARE_MUSIC,
  CLOUDFLARE_SPEECH,
  CLOUDFLARE_TRANSCRIPTION,
  AUDIO_PRICING_VERSION,
  CLOUDFLARE_CLAUDE,
  CLAUDE_PRICING_VERSION,
  H3,
  SUNBURST,
  GPT6_SOL,
  GPT6_LUNA,
  MEDIA2_PRICING_VERSION,
  OPENAI_PRICING_VERSION,
} from './provider-contracts';

/** Which kinds a profile of each pipeline must route; the other pipeline's kinds are not used. */
export function pipelineKinds(pipeline?: 2) {
  const skip = pipeline === 2 ? PIPELINE1_JOBS : PIPELINE2_JOBS;
  return jobKinds.filter((kind) => kind !== 'ASSEMBLE' && !skip.has(kind));
}
/** The writing jobs GPT-6 Sol may do, and the judging jobs GPT-6 Luna may do: never both. */
export const SOL_JOBS = new Set<JobKind>([
  'PITCH',
  'SEASON',
  'STORY',
  'PUNCH_UP',
  'BREAKDOWN',
  'SOFTEN',
]);
export const LUNA_JOBS = new Set<JobKind>([
  'PANEL',
  'QA_CONTENT',
  'QA_REFERENCE',
  'QA_SET',
  'QA_VIDEO',
  'EPISODE_REVIEW',
]);

export const CHAT_JOBS = new Set<JobKind>([
  'PITCH',
  'SEASON',
  'SCRIPT',
  'SCRIPT_DOCTOR',
  'SCRIPT_REVIEW',
  'REVISION',
  'SOFTEN',
  'QA_CONTENT',
  'QA_REFERENCE',
  'QA_SET',
]);
export function defaultModel(kind: JobKind, videoModel: string = MODELS.video): string {
  if (kind === 'VIDEO') return videoModel;
  if (['CAST_IMAGE', 'REFERENCE', 'SET_IMAGE'].includes(kind)) return MODELS.image;
  if (kind === 'SPEECH') return MODELS.speech;
  if (kind === 'CAPTIONS') return MODELS.transcription;
  if (kind === 'SCORE') return MODELS.music;
  return kind === 'ASSEMBLE' ? 'ffmpeg' : MODELS.text;
}

export const ProfileSchema = z
  .object({
    id: z
      .string()
      .regex(/^cloudflare-[a-z0-9-]+$/)
      .max(80),
    version: z.string().min(1).max(80),
    scope: z.enum(['pilot', 'production']),
    maxCostMicros: z.number().int().positive().optional(),
    credentialPolicy: z.literal('cloudflare-only').optional(),
    /** Pipeline 2 writes with a writers' room and films multi-cut sequences; absent is Pipeline 1. */
    pipeline: z.literal(2).optional(),
    // All AI steps must be explicit: there is no implicit Google fallback.
    steps: z.record(z.enum(jobKinds), ProviderRouteSchema.optional()),
  })
  .strict()
  .superRefine((profile, ctx) => {
    for (const kind of pipelineKinds(profile.pipeline)) {
      const route = profile.steps[kind];
      if (!route) {
        ctx.addIssue({ code: 'custom', message: `Missing route for ${kind}` });
        continue;
      }
      if (
        profile.scope === 'production' &&
        profile.credentialPolicy === 'cloudflare-only' &&
        route.adapter === 'google-native'
      )
        ctx.addIssue({
          code: 'custom',
          message: `Choose a Unified Billing adapter for ${kind} before enabling full production.`,
        });
      try {
        validateRoute(kind, route);
      } catch (e) {
        ctx.addIssue({ code: 'custom', message: String(e) });
      }
    }
    if (profile.id === 'cloudflare-google-pilot' && profile.scope !== 'pilot')
      ctx.addIssue({
        code: 'custom',
        message: 'Google baseline is restricted to a single-shot pilot.',
      });
    if (
      profile.scope === 'production' &&
      !jobKinds.some((kind) => {
        const route = profile.steps[kind];
        return route && (route.adapter === 'chat' || route.model !== defaultModel(kind));
      })
    )
      ctx.addIssue({
        code: 'custom',
        message: 'Full episode trials require a candidate model configuration.',
      });
  });
export type ProviderProfile = z.infer<typeof ProfileSchema>;

export function validateRoute(kind: JobKind, route: ProviderRoute) {
  if (route.adapter === 'openai-unified') {
    if (route.model === GPT6_SOL ? !SOL_JOBS.has(kind) : !LUNA_JOBS.has(kind))
      throw new Error(`Unsupported ${route.model} capability for ${kind}`);
    return;
  }
  if (route.adapter === 'anthropic-unified') {
    if (!['PITCH', 'SEASON', 'SCRIPT', 'SCRIPT_DOCTOR', 'REVISION', 'SOFTEN'].includes(kind))
      throw new Error(`Unsupported Claude writing capability for ${kind}`);
    return;
  }
  if (route.adapter === 'cloudflare-audio') {
    const models: Partial<Record<JobKind, string>> = {
      SCORE: CLOUDFLARE_MUSIC,
      SPEECH: CLOUDFLARE_SPEECH,
      CAPTIONS: CLOUDFLARE_TRANSCRIPTION,
    };
    if (models[kind] !== route.model)
      throw new Error(`Unsupported Cloudflare audio capability for ${kind}`);
    return;
  }
  if (route.adapter === 'google-native') {
    const allowed =
      kind === 'VIDEO' ? [MODELS.video, MODELS.controlledVideo] : [defaultModel(kind)];
    if (!allowed.includes(route.model) || kind === 'ASSEMBLE')
      throw new Error(`Unsupported Google capability/model for ${kind}: ${route.model}`);
    return;
  }
  if (route.adapter === 'google-unified') {
    if (
      !CHAT_JOBS.has(kind) &&
      !['QA_VIDEO', 'EPISODE_REVIEW', 'EDIT_PLAN', 'QA_PERFORMANCE'].includes(kind)
    )
      throw new Error(`Unsupported Gemini unified capability for ${kind}`);
    return;
  }
  if (route.adapter === 'cloudflare-media') {
    if (route.virtualAvatars !== undefined && route.model !== SEEDANCE_FAST)
      throw new Error('Virtual avatars apply only to Seedance video inputs.');
    if (
      (route.model === H3 || route.model === SUNBURST) !==
      (route.pricingVersion === MEDIA2_PRICING_VERSION)
    )
      throw new Error(`Use the matching rate card for ${route.model}.`);
    if (
      route.model === SEEDANCE_FAST || route.model === H3
        ? kind !== 'VIDEO'
        : !['REFERENCE', 'CAST_IMAGE', 'SET_IMAGE'].includes(kind)
    )
      throw new Error(`Unsupported Cloudflare media capability for ${kind}`);
    return;
  }
  if (!CHAT_JOBS.has(kind)) throw new Error(`${kind} requires a native media adapter.`);
  if (new Set(route.candidates.map((c) => c.model)).size !== route.candidates.length)
    throw new Error('Candidate models must be unique.');
  if (
    route.candidates.some((c) => !c.model.includes('/') || /^(dynamic|cloudflare)\//.test(c.model))
  )
    throw new Error('Candidates must name concrete upstream models.');
  if (
    route.model !== 'cloudflare/auto' &&
    (route.candidates.length !== 1 || route.candidates[0].model !== route.model)
  )
    throw new Error('A fixed route must have exactly its own model in the rate card.');
}

export function googlePilotProfile(): ProviderProfile {
  return ProfileSchema.parse({
    id: 'cloudflare-google-pilot',
    version: '1',
    scope: 'pilot',
    steps: Object.fromEntries(
      pipelineKinds().map((kind) => [
        kind,
        { adapter: 'google-native', model: defaultModel(kind) },
      ]),
    ),
  });
}

export function configuredProfiles(raw?: string): ProviderProfile[] {
  const profiles = raw ? z.array(ProfileSchema).max(10).parse(JSON.parse(raw)) : [];
  if (
    new Set(profiles.map((p) => p.id)).size !== profiles.length ||
    profiles.some((p) => p.id === 'cloudflare-google-pilot')
  )
    throw new Error('Profile IDs must be unique and cannot replace the Google pilot.');
  return [googlePilotProfile(), ...profiles];
}

export function routeFor(profile: ProviderProfile, kind: JobKind): Routing | undefined {
  if (kind === 'ASSEMBLE') return undefined;
  const route = profile.steps[kind];
  if (!route) throw new Error(`Missing route for ${kind}`);
  return {
    profile: profile.id,
    version: profile.version,
    transport: 'cloudflare',
    ...(profile.credentialPolicy ? { credentialPolicy: profile.credentialPolicy } : {}),
    route,
  };
}

export function routeReservation(
  kind: JobKind,
  model: string,
  duration?: number,
  routing?: Routing,
) {
  if (routing?.route.adapter === 'cloudflare-audio')
    return kind === 'SCORE' ? 200_000 : kind === 'SPEECH' ? 20_000 : 2_000;
  if (routing?.route.adapter === 'cloudflare-media')
    return model === SEEDREAM
      ? 250_000
      : model === SUNBURST
        ? 400_000
        : model === H3
          ? // $0.08 a second at 768P, with a quarter more held back.
            Math.ceil(Math.min(15, Math.max(4, Math.ceil(duration ?? 15))) * 80_000 * 1.25)
          : Math.ceil(Math.max(4, Math.ceil(duration ?? 10)) * 120_000 * 1.25);
  return routing?.route.adapter === 'chat' ||
    routing?.route.adapter === 'google-unified' ||
    routing?.route.adapter === 'anthropic-unified' ||
    routing?.route.adapter === 'openai-unified'
    ? routing.route.reservationMicros
    : reserveFor(kind, model, duration);
}

/** Scale the existing conservative episode quote to cover each configured step's reservation. */
export function profileAllowance(base: number, profile?: ProviderProfile) {
  const factor = profile
    ? Math.max(
        1,
        ...jobKinds.map((kind) => {
          const route = profile.steps[kind];
          return route?.adapter === 'chat' ||
            route?.adapter === 'google-unified' ||
            route?.adapter === 'anthropic-unified' ||
            route?.adapter === 'openai-unified'
            ? route.reservationMicros / reserveFor(kind, defaultModel(kind))
            : 1;
        }),
      )
    : 1;
  return Math.ceil(base * factor);
}

/** Opt-in, one-shot candidate preset; existing Google judges stay constant. */
export function seedanceFastPilotProfile(): ProviderProfile {
  const profile = googlePilotProfile();
  for (const kind of ['REFERENCE', 'CAST_IMAGE', 'SET_IMAGE', 'VIDEO'] as const)
    profile.steps[kind] = {
      adapter: 'cloudflare-media',
      model: kind === 'VIDEO' ? SEEDANCE_FAST : SEEDREAM,
      pricingVersion: MEDIA_PRICING_VERSION,
    };
  for (const kind of jobKinds)
    if (CHAT_JOBS.has(kind) || ['QA_VIDEO', 'EPISODE_REVIEW', 'EDIT_PLAN'].includes(kind))
      profile.steps[kind] = {
        adapter: 'google-unified',
        model: CLOUDFLARE_GEMINI,
        pricingVersion: GEMINI_PRICING_VERSION,
        reservationMicros: reserveFor(kind, MODELS.text),
      };
  return ProfileSchema.parse({
    ...profile,
    id: 'cloudflare-seedance-fast-pilot',
    version: '1',
    credentialPolicy: 'cloudflare-only',
    maxCostMicros: 5_000_000,
  });
}

/** Opt-in complete creator journey; all AI steps use Cloudflare account credentials. */
export function seedanceFastEpisodeProfile(): ProviderProfile {
  const profile = seedanceFastPilotProfile();
  profile.steps.VIDEO = { ...profile.steps.VIDEO!, virtualAvatars: true } as ProviderRoute;
  for (const kind of ['EDIT_PLAN', 'EPISODE_REVIEW'] as const)
    profile.steps[kind] = {
      ...profile.steps[kind]!,
      reservationMicros: 400_000,
    } as ProviderRoute;
  for (const kind of ['PITCH', 'SEASON', 'SCRIPT', 'SCRIPT_DOCTOR', 'REVISION', 'SOFTEN'] as const)
    profile.steps[kind] = {
      adapter: 'anthropic-unified',
      model: CLOUDFLARE_CLAUDE,
      pricingVersion: CLAUDE_PRICING_VERSION,
      reservationMicros: 750_000,
    };
  for (const [kind, model] of Object.entries({
    SCORE: CLOUDFLARE_MUSIC,
    SPEECH: CLOUDFLARE_SPEECH,
    CAPTIONS: CLOUDFLARE_TRANSCRIPTION,
  }))
    profile.steps[kind as JobKind] = {
      adapter: 'cloudflare-audio',
      model: model as
        typeof CLOUDFLARE_MUSIC | typeof CLOUDFLARE_SPEECH | typeof CLOUDFLARE_TRANSCRIPTION,
      pricingVersion: AUDIO_PRICING_VERSION,
    };
  return ProfileSchema.parse({
    ...profile,
    id: 'cloudflare-seedance-fast-episode',
    version: '4',
    scope: 'production',
    maxCostMicros: 15_000_000,
  });
}

/**
 * Pipeline 2, opt-in: GPT-6 Sol writes and directs, GPT-6 Luna judges, GPT Image 2.5 Sunburst
 * draws, MiniMax H3 films at 768P, Gemini watches only for lip sync and acting. Approved by the
 * owner on October 2, 2026. Everything is billed through the Cloudflare account.
 */
export function h3SolEpisodeProfile(): ProviderProfile {
  const sol = (reservationMicros: number) =>
    ({
      adapter: 'openai-unified',
      model: GPT6_SOL,
      pricingVersion: OPENAI_PRICING_VERSION,
      reservationMicros,
      reasoningEffort: 'high',
    }) as const;
  const luna = (reservationMicros: number, reasoningEffort: 'medium' | 'high' = 'medium') =>
    ({
      adapter: 'openai-unified',
      model: GPT6_LUNA,
      pricingVersion: OPENAI_PRICING_VERSION,
      reservationMicros,
      reasoningEffort,
    }) as const;
  const media = (model: typeof H3 | typeof SUNBURST) =>
    ({ adapter: 'cloudflare-media', model, pricingVersion: MEDIA2_PRICING_VERSION }) as const;
  const audio = (
    model: typeof CLOUDFLARE_MUSIC | typeof CLOUDFLARE_SPEECH | typeof CLOUDFLARE_TRANSCRIPTION,
  ) => ({ adapter: 'cloudflare-audio', model, pricingVersion: AUDIO_PRICING_VERSION }) as const;
  return ProfileSchema.parse({
    id: 'cloudflare-h3-sol-episode',
    version: '1',
    scope: 'production',
    pipeline: 2,
    credentialPolicy: 'cloudflare-only',
    maxCostMicros: 20_000_000,
    steps: {
      PITCH: sol(600_000),
      SEASON: sol(900_000),
      STORY: sol(900_000),
      PUNCH_UP: sol(900_000),
      BREAKDOWN: sol(1_200_000),
      SOFTEN: sol(400_000),
      PANEL: luna(100_000, 'high'),
      QA_CONTENT: luna(50_000),
      QA_SET: luna(100_000),
      QA_REFERENCE: luna(100_000),
      QA_VIDEO: luna(150_000),
      EPISODE_REVIEW: luna(200_000, 'high'),
      QA_PERFORMANCE: {
        adapter: 'google-unified',
        model: CLOUDFLARE_GEMINI,
        pricingVersion: GEMINI_PRICING_VERSION,
        reservationMicros: 400_000,
      },
      CAST_IMAGE: media(SUNBURST),
      SET_IMAGE: media(SUNBURST),
      REFERENCE: media(SUNBURST),
      VIDEO: media(H3),
      SCORE: audio(CLOUDFLARE_MUSIC),
      SPEECH: audio(CLOUDFLARE_SPEECH),
      CAPTIONS: audio(CLOUDFLARE_TRANSCRIPTION),
    },
  });
}

function cohortProfileId(userId: string, raw?: string): string | undefined {
  return z.record(z.string(), z.string()).parse(JSON.parse(raw ?? '{}'))[userId];
}

/** Owner-controlled cohort only. Clients cannot select their own experimental providers. */
export function consumerProviderProfile(
  userId: string,
  env: Record<string, string | undefined>,
): ProviderProfile | undefined {
  const id = cohortProfileId(userId, env.CONSUMER_PROVIDER_PROFILES_JSON);
  if (!id) return undefined;
  if (env.ALLOW_CLOUDFLARE_PRODUCTION !== 'true')
    throw new Error('Creator provider trial is disabled.');
  const profile = configuredProfiles(env.CLOUDFLARE_PROFILES_JSON).find((p) => p.id === id);
  if (!profile || profile.scope !== 'production' || profile.credentialPolicy !== 'cloudflare-only')
    throw new Error('Creator trials require an enabled Cloudflare-only production profile.');
  return profile;
}

export const LAB_PROVIDER_PROFILE = 'cloudflare-h3-sol-episode';
class LabProviderConfigurationError extends Error {}

/** Explicit member assignments retain their owner-granted personal spending requirement. */
export function labRequiresAllowance(userId: string, env: Record<string, string | undefined>) {
  return Boolean(cohortProfileId(userId, env.LAB_PROVIDER_PROFILES_JSON));
}

/** All staff use Pipeline 2; neither missing configuration nor client input selects Google. */
export function labProviderProfile(
  userId: string,
  env: Record<string, string | undefined>,
): ProviderProfile {
  const id = cohortProfileId(userId, env.LAB_PROVIDER_PROFILES_JSON) ?? LAB_PROVIDER_PROFILE;
  if (id !== LAB_PROVIDER_PROFILE)
    throw new LabProviderConfigurationError(
      'Lab members must use the cloudflare-h3-sol-episode Pipeline 2 profile. Contact the Lab owner.',
    );
  if (env.ALLOW_CLOUDFLARE_PRODUCTION !== 'true')
    throw new LabProviderConfigurationError(
      'Lab Pipeline 2 generation is disabled. Contact the Lab owner.',
    );
  const profile = configuredProfiles(env.CLOUDFLARE_PROFILES_JSON).find((p) => p.id === id);
  if (
    !profile ||
    profile.pipeline !== 2 ||
    profile.scope !== 'production' ||
    profile.credentialPolicy !== 'cloudflare-only'
  )
    throw new LabProviderConfigurationError(
      'Lab Pipeline 2 requires an enabled Cloudflare-only production profile. Contact the Lab owner.',
    );
  return profile;
}

export function labSeriesGenerationError(started?: {
  providerProfile?: string;
  providerConfiguration?: { pipeline?: number };
}) {
  return started &&
    (started.providerProfile !== LAB_PROVIDER_PROFILE ||
      started.providerConfiguration?.pipeline !== 2)
    ? 'This series started on an older pipeline. Its history is preserved. Create a new series to generate with Pipeline 2.'
    : undefined;
}

/** Read-only presentation state. Generation must still use the strict profile resolver above. */
export function labProviderConfiguration(
  userId: string,
  env: Record<string, string | undefined>,
): {
  profiles: ProviderProfile[];
  managedProfileId?: string;
  allowanceRequired?: boolean;
  generationUnavailable?: string;
} {
  try {
    const profile = labProviderProfile(userId, env);
    return {
      profiles: [profile],
      managedProfileId: profile.id,
      allowanceRequired: labRequiresAllowance(userId, env),
    };
  } catch (error) {
    // Do not expose parser errors or configuration contents to the browser, or silently switch
    // a managed member to Google when the owner's chosen provider cannot be used.
    return {
      profiles: [],
      managedProfileId: LAB_PROVIDER_PROFILE,
      generationUnavailable:
        error instanceof LabProviderConfigurationError
          ? error.message
          : 'Generation is unavailable because the Lab provider configuration needs attention. Contact the Lab owner.',
    };
  }
}
